import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Sparkles, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { submitAssessment } from "../lib/api";
import { QUIZ } from "../lib/data";

export const Quiz = ({ onClose, onEnroll, onCounselling }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const pick = async (optionId) => {
    const next = [...answers, optionId];
    setAnswers(next);
    if (step < QUIZ.length - 1) {
      setStep(step + 1);
    } else {
      setLoading(true);
      try {
        const res = await submitAssessment(next);
        setResult(res);
      } catch {
        toast.error("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const q = QUIZ[step];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-[16px] p-4"
        onClick={onClose}
        data-testid="quiz-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-xl rounded-3xl border border-white/10 bg-elevated p-7 sm:p-10"
          data-testid="quiz-modal"
        >
          <button
            data-testid="quiz-close-button"
            onClick={onClose}
            className="absolute right-5 top-5 rounded-full border border-white/15 p-2 text-neutral-400 hover:text-champagne hover:border-gold/50 transition-colors"
          >
            <X size={16} />
          </button>

          {result ? (
            <div data-testid="quiz-result">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold/90">Your perfect match</p>
              <h3 className="mt-4 font-display text-2xl sm:text-3xl leading-snug text-neutral-100">{result.course.title}</h3>
              <p className="mt-4 text-sm sm:text-base font-light text-neutral-400 leading-relaxed">{result.reason}</p>
              <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-xl border border-white/10 bg-void/50 p-3">
                  <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-neutral-500">Program</p>
                  <p className="mt-1 text-xs text-champagne">{result.course.type}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-void/50 p-3">
                  <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-neutral-500">Metro jobs</p>
                  <p className="mt-1 text-xs text-champagne">{result.course.salary}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-void/50 p-3">
                  <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-neutral-500">Freelance</p>
                  <p className="mt-1 text-xs text-champagne">{result.course.freelance}</p>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  data-testid="quiz-result-enroll-button"
                  onClick={() => onEnroll(result.course)}
                  className="flex-1 rounded-full bg-leaf px-6 py-3.5 text-sm font-semibold text-void hover:bg-champagne transition-colors"
                >
                  Enroll Now · ₹{result.course.price.toLocaleString("en-IN")}
                </button>
                <button
                  data-testid="quiz-result-counselling-button"
                  onClick={onCounselling}
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3.5 text-sm text-neutral-200 hover:border-gold/60 hover:text-champagne transition-colors"
                >
                  <PhoneCall size={14} /> Free Mentor Call
                </button>
              </div>
            </div>
          ) : (
            <div data-testid="quiz-step-view">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold/90">
                  Free Career Assessment · {Math.round(((step + 1) / QUIZ.length) * 5) || 1} min
                </p>
                <p className="font-mono text-xs text-neutral-500">{step + 1} / {QUIZ.length}</p>
              </div>
              <div className="mt-4 h-1 overflow-hidden rounded-full bg-white/10">
                <motion.div
                  className="h-full bg-leaf"
                  animate={{ width: `${((step + 1) / QUIZ.length) * 100}%` }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                  <h3 className="mt-8 font-display text-2xl sm:text-3xl text-neutral-100">{q.question}</h3>
                  <div className="mt-7 space-y-3">
                    {q.options.map((o) => (
                      <button
                        key={o.id}
                        data-testid={`quiz-option-${o.id}`}
                        disabled={loading}
                        onClick={() => pick(o.id)}
                        className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-void/40 px-5 py-4 text-left text-sm text-neutral-200 transition-all duration-300 hover:translate-x-1.5 hover:border-gold/70 hover:bg-gold/10 disabled:opacity-50"
                      >
                        {o.label}
                        <ArrowRight size={14} className="text-gold" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>

              {step > 0 && (
                <button
                  data-testid="quiz-back-button"
                  onClick={() => { setStep(step - 1); setAnswers(answers.slice(0, -1)); }}
                  className="mt-6 inline-flex items-center gap-2 text-xs text-neutral-500 hover:text-champagne transition-colors"
                >
                  <ArrowLeft size={12} /> Previous question
                </button>
              )}
              {loading && (
                <p className="mt-6 inline-flex items-center gap-2 text-sm text-champagne" data-testid="quiz-loading">
                  <Sparkles size={14} className="animate-pulse" /> Analysing your answers…
                </p>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
